import { Box } from "@chakra-ui/react/box";
import { Circle } from "@chakra-ui/react/circle";
import { Flex } from "@chakra-ui/react/flex";
import { Float } from "@chakra-ui/react/float";
import { Icon } from "@chakra-ui/react/icon";
import { MenuItem } from "@chakra-ui/react/menu";

import { useUnreadCounts } from "hooks/queries/use-current-agent";
import React from "react";
import {
  LuBot,
  LuCalendar,
  LuChartArea,
  LuCircuitBoard,
  LuCreditCard,
  LuMail,
  LuMegaphone,
  LuSettings,
  LuStar,
  LuUser,
} from "react-icons/lu";
import { NavLink } from "react-router-dom";
import { useAppSelector } from "redux/hooks";

interface MobileBrowserNavigationButtonProps {
  icon: any;
  name: string;
  link: string;
  hasNotifications?: boolean;
  isDisabled?: boolean;
}

const MobileBrowserNavigationButton = ({
  icon,
  hasNotifications = false,
  name,
  link,
  isDisabled,
}: MobileBrowserNavigationButtonProps) => {
  const { colorScheme } = useAppSelector((state) => state.theme);

  return (
    <Box
      css={{
        "& a.selected > div": {
          bgColor: `${colorScheme}.solid`,
        },
        "& > a.selected svg": {
          color: "white",
        },
      }}
    >
      <NavLink
        to={link}
        className={({ isActive }) => (isActive ? "selected" : "")}
      >
        <MenuItem value={name} disabled={isDisabled}>
          <Flex position="relative" alignItems="center" justifyContent="center">
            <Icon as={icon} width="2rem" height="2rem" color="fg.default" />
            {hasNotifications ? (
              <Float>
                <Circle
                  zIndex="1"
                  height="1rem"
                  width="1rem"
                  bgColor="red.solid"
                />
              </Float>
            ) : null}
          </Flex>{" "}
          {name}
        </MenuItem>
      </NavLink>
    </Box>
  );
};

export const InboxLink = () => {
  const { data: unreadCounts = [] } = useUnreadCounts();
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  const unreadCount = unreadCounts.find((uc) => uc.merchant_id === merchantId);

  return (
    <MobileBrowserNavigationButton
      hasNotifications={!!unreadCount?.personal || !!unreadCount?.shared}
      icon={LuMail}
      name="Inbox"
      link={`/${merchantId}/inbox`}
    />
  );
};

export const ContactsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <MobileBrowserNavigationButton
      icon={LuUser}
      name="Contacts"
      link={`/${merchantId}/contacts`}
    />
  );
};

export const AiChatsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <MobileBrowserNavigationButton
      icon={LuBot}
      name="AI Chats"
      link={`/${merchantId}/ai-chats`}
    />
  );
};

export const CalendarLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <MobileBrowserNavigationButton
      icon={LuCalendar}
      name="Calendar"
      link={`/${merchantId}/calendar`}
    />
  );
};

export const PaymentsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <MobileBrowserNavigationButton
      icon={LuCreditCard}
      name="Payments"
      link={`/${merchantId}/payments`}
    />
  );
};

export const AutomationsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <MobileBrowserNavigationButton
      icon={LuCircuitBoard}
      name="Automations"
      link={`/${merchantId}/automations`}
    />
  );
};

export const CampaignsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <MobileBrowserNavigationButton
      icon={LuMegaphone}
      name="Campaigns"
      link={`/${merchantId}/campaigns`}
    />
  );
};

export const ReviewsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <MobileBrowserNavigationButton
      icon={LuStar}
      name="Reviews"
      link={`/${merchantId}/reviews`}
    />
  );
};

export const AnalyticsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <MobileBrowserNavigationButton
      icon={LuChartArea}
      name="Analytics"
      link={`/${merchantId}/analytics`}
    />
  );
};

export const SettingsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <MobileBrowserNavigationButton
      icon={LuSettings}
      name="Settings"
      link={`/${merchantId}/settings`}
    />
  );
};

export default MobileBrowserNavigationButton;
